import React, {useEffect, useRef, useState} from 'react';
import SelectField from "../Select";
import {components} from "react-select";
import CheckBox from "../CheckBox";
import ButtonBase from "../ButtonBase";
import SearchIcon from '../../assets/svgs/search.svg'
import './MultiSelect.scss';
import Text from "../Text";
import PropTypes from "prop-types";
import useRunOnceWhenTruthy from "../../hooks/useRunOnceWhenTruthy";

const Option = (props) => {
  const label2 = props?.data?.label2;
  return (
    <div>
      <components.Option {...props}>
        <div className="Suc-multiLine-option">
          <CheckBox
            className={'Suc-multiselect-checkbox'}
            checked={props.isSelected}
          />
          <div className={'Suc-labels'}>
            <Text fontWeight={600} className={'Suc-first-line'}>{props.label}</Text>
            {label2 && <Text>{props?.data?.label2}</Text>}
          </div>
        </div>
      </components.Option>
    </div>
  );
};

const allOption = {
  label: "All",
  label2: 'Select all',
  value: "*"
};

const ValueContainer = ({children, selectProps, ...props}) => {
  const currentValues = props.getValue();
  const optionName = selectProps?.selectProps?.optionName
  let text = `${currentValues?.length} ${optionName} selected`
  if (currentValues.some(val => val.value === allOption.value)) {
    text = `All ${optionName} (${currentValues?.length - 1})`;
  }

  const newChildren = [
    <Text key={0}>{text}</Text>,
    children[1]
  ]

  return (
    <components.MultiValueLabel {...props}>
      {newChildren}
    </components.MultiValueLabel>
  );
};

const MenuList = (props) => {
  const {textFilter, setTextFilter, onApplyClick, onCancelClick} = props.selectProps?.selectProps
  return (
    <div className={'Suc-multiselect-menu'}>
      <div className="Suc-search">
        <SearchIcon className={'Suc-search-icon'}/>
        <CustomSearch type={'search'}
                      textFilter={textFilter}
                      setTextFilter={setTextFilter}
                      selectProps={props.selectProps}/>
      </div>
      <components.MenuList {...props} />
      <div className="Suc-actions">
        <ButtonBase className="Suc-cancel" onClick={onCancelClick}>Clear</ButtonBase>
        <ButtonBase className="Suc-apply" onClick={onApplyClick}>Apply</ButtonBase>
      </div>
    </div>
  );
}

const MultiSelect = (props) => {
  const {optionName} = props;
  const [innerValue, setInnerValue] = useState([]);
  const [textFilter, setTextFilter] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef(null);

  const options = props.options?.length ? [allOption, ...props.options] : [];
  const defaultValue = props.defaultValue || options;

  useRunOnceWhenTruthy(() => {
    setInnerValue(defaultValue);
  }, props.options)

  const onApplyClick = () => {
    setIsMenuOpen(false);
    props.onChange(innerValue.filter(({value}) => value !== allOption.value))
  }

  const onCancelClick = () => {
    setTextFilter('')
    setInnerValue(defaultValue)
  }

  useEffect(() => {
    const onDomClick = (e) => {
      if (!containerRef.current.contains(e.target)) {
        onApplyClick();
      }
    };

    document.addEventListener("mousedown", onDomClick);

    return () => {
      document.removeEventListener("mousedown", onDomClick);
    };
  }, [innerValue]);

  return <div ref={containerRef} className={`Suc-multi-select ${props.className}`}><SelectField
    isMulti
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    allowSelectAll={true}
    isSearchable={false}
    onMenuInputFocus={() => setIsFocused(true)}
    onMenuOpen={() => setIsMenuOpen(prev => !prev)}
    onInputChange={val => setTextFilter(val)}
    inputValue={textFilter}
    menuIsOpen={isMenuOpen}
    isFocused={isFocused || undefined}
    components={{
      Option,
      MenuList,
      ValueContainer
    }}
    {...props}
    options={options}
    onChange={(selected, event) => {
      if (selected !== null && selected.length > 0) {
        if (selected[selected.length - 1].value === allOption.value) { // if select all was clicked, select all options
          return setInnerValue(options);
        }
        let result = [];
        if (selected.length === props.options.length) { // if all options selected ony by one, selecting also 'select All' option
          if (selected.includes(allOption)) { // if all was selected and one option was deselected, deselect also 'select all option
            result = selected.filter(
              option => option.value !== allOption.value
            );
          } else if (event.action === "select-option") {
            result = options;
          }
          return setInnerValue(result);
        }
      }

      return setInnerValue(selected);
    }}
    value={innerValue}
    selectProps={{
      textFilter,
      setTextFilter,
      optionName,
      onApplyClick,
      onCancelClick
    }}
  /></div>
}

const CustomSearch = ({textFilter, setTextFilter, selectProps}) => {
  const {onMenuInputFocus} = selectProps;
  return <input
    className={'Suc-search-text'}
    autoCorrect="off"
    autoComplete="off"
    spellCheck="false"
    type="text"
    value={textFilter}
    onChange={(e) =>
      setTextFilter(e.currentTarget.value, {
        action: "input-change"
      })
    }
    onMouseDown={(e) => {
      e.stopPropagation();
      e.target.focus();
    }}
    onTouchEnd={(e) => {
      e.stopPropagation();
      e.target.focus();
    }}
    onFocus={onMenuInputFocus}
    placeholder="Search"
  />
}

const optionShape = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node,
  label2: PropTypes.node
}

MultiSelect.propTypes = {
  /** custom classname */
  className: PropTypes.string,
  /** label */
  label: PropTypes.string,
  /** remove margin-bottom (to allow inline alignment) */
  removeBottomMargin: PropTypes.bool,
  /** options array */
  options: PropTypes.arrayOf(PropTypes.shape(optionShape)),
  /** default selected options */
  defaultValue: PropTypes.arrayOf(PropTypes.shape(optionShape)),
  /** show loader */
  isLoading: PropTypes.bool,
  /** Option name (all [optionName] selected) */
  optionName: PropTypes.string,
}

MultiSelect.defaultProps = {
  className: '',
  disabled: false,
  removeBottomMargin: false,
  enableOptionsAdding: false,
  optionName: 'Options',
  defaultValue: null
}
export default MultiSelect;
